import groupBy from "lodash/groupBy";
import { createSelector } from "reselect";

import { EMPTY_ARRAY } from "lib";
import { reduceById } from "lib/reducers";
import { attributeOrderSort } from "lib/speciesAttributes";

import { getCurrentSale } from "./currentSale";
import { getAllProducts } from "./root";

// Only return products that are not deleted.
export const selectActiveProducts = createSelector(
  [getAllProducts],
  (products: Record<string, Product>): Record<string, Product> =>
    reduceById(Object.values(products).filter(p => !p.deleted)),
);

// The two (state, ...) input selectors below just pluck their positional arg
// back out for the combiner - reselect's mechanism for memoizing on a raw
// argument alongside a computed one, not something specific to this selector.
export const getProductsSelector = createSelector(
  [
    selectActiveProducts,
    (_state: State, speciesId?: number | null) => speciesId,
    (_state: State, _speciesId?: number | null, deploymentId?: number | null) =>
      deploymentId,
  ],
  (
    products: Record<string, Product>,
    speciesId: number | null | undefined,
    deploymentId: number | null | undefined,
  ): Product[] =>
    Object.values(products)
      .filter(
        product =>
          (!speciesId || product.species_id === speciesId) &&
          (!deploymentId || product.deployment_id === deploymentId),
      )
      .sort(attributeOrderSort),
);

export const getProductsBySpecies = createSelector(
  [selectActiveProducts, getCurrentSale],
  (products: Record<string, Product>, sale: Sale | null): Product[] =>
    Object.values(products).filter(p => p.species_id === sale?.species_id),
);

const selectDeploymentProductsByDeploymentIdLookup = createSelector(
  [getProductsBySpecies],
  (products: Product[]): Record<string, Product[]> =>
    groupBy(products, "deployment_id"),
);

export const getProductsByDeploymentId =
  (deploymentId: number) => (state: State) =>
    selectDeploymentProductsByDeploymentIdLookup(state)[deploymentId] ||
    EMPTY_ARRAY;

export const selectProductsByDeploymentIdAndSpeciesId =
  (deploymentId: number, speciesId: number) => (state: State) => {
    const products = selectActiveProducts(state);
    return Object.values(products).filter(
      p => p.deployment_id === deploymentId && p.species_id === speciesId,
    );
  };
